<template>
  <v-list-group value="favorites">
    <template #activator="{ props }">
      <v-list-item
        v-tooltip:top="t('navigation.drawer.favorites.tooltip')"
        v-bind="props"
        prepend-icon="star no-fill"
        active-class="v-list-item__opened"
      >
        <span
          class="text--line-height-normal font-weight-medium text-body-medium"
          data-cy="navigation-drawer-favorites-menu"
        >
          {{ t('navigation.drawer.favorites.label') }}
        </span>
      </v-list-item>
    </template>
    <div
      v-if="favoriteItems.length === 0"
      class="text--line-height-normal text-color_f font-italic pl-13 pb-4 pt-3"
    >
      {{ t('navigation.drawer.favorites.noFavorites') }}
    </div>
    <KpNavigationDrawerItem
      v-for="element in favoriteItems"
      v-else-if="touchDevice"
      :key="element.url"
      always-visible
      :disable-active="true"
      :enable-router="true"
      class="kp-navigation-dashboards-item rounded"
      value-prefix="favorite"
      :title="t(element.title)"
      :tooltip="t(element.tooltip) "
      :to="element.url"
      :data-cy="`favorite-${encodeURIComponent(element.title)}`"
    />
    <!-- Draggable list of favorite items for non touch device-->
    <draggable
      v-else
      :list="favoriteItems"
      item-key="url"
      animation="200"
      class="fav-draggable"
      ghost-class="opacity-30"
      chosen-class="opacity-80"
      @end="onDragEnd"
    >
      <template #item="{ element }">
        <KpNavigationDrawerItem
          always-visible
          :disable-active="true"
          :enable-router="true"
          class="kp-navigation-dashboards-item rounded"
          value-prefix="favorite"
          :title="t(element.title)"
          :tooltip="t(element.tooltip) "
          :to="element.url"
          :data-cy="`favorite-${encodeURIComponent(element.title)}`"
        />
      </template>
    </draggable>
  </v-list-group>
</template>

<script setup>
import {
  computed, defineAsyncComponent, onMounted, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import useFavorites from '~/composables/useFavorites';
import useNavigationDrawer from '~/composables/useNavigationDrawer';

import KpNavigationDrawerItem from './KpNavigationDrawerItem.vue';

const draggable = defineAsyncComponent(() => import('vuedraggable'));

// Access the internationalization (i18n) functionality
const { t } = useI18n();

// Extract navigation drawer items and related functions
const {
  rawDashboardList,
} = useNavigationDrawer();

// Extract favorites-related functionality
const {
  debouncedUpdateFavorites, loadFavorites, onFavoritesResult, userFavorites,
} = useFavorites();

/**
 * Computed property to map user favorites to their respective menu items.
 * Must match the URLs in the navigation drawer and dashboard lists.
 */
const favoriteItems = computed(() => {
  const allMenuItems = [
    ...rawDashboardList.value.flatMap((category) => category.items),
    {
      url: '/profile', // Allow profile page
      title: 'common.settings',
      tooltip: 'common.settings',
    },
  ];
  const favorites = Array.isArray(userFavorites.value) ? userFavorites.value : [];
  // Filter and map the user's favorite URLs to the corresponding menu items
  return favorites.reduce((acc, url) => {
    const menuItem = allMenuItems.find((item) => item.url === url);
    if (menuItem) {
      acc.push(menuItem);
    }
    return acc;
  }, []);
});

/**
 * Handler for the end of a drag-and-drop event.
 */
function onDragEnd() {
  userFavorites.value = favoriteItems.value.map((item) => item.url);
}

// Prevent syncing favorites with the server during the initial load
const firstLoadDone = ref(false);

/**
 * Initiates the loading of favorites from the server to populate the user's favorites list.
 */
onMounted(async () => {
  await loadFavorites(); // Load favorites data from the server
});

/**
 * Parses and updates the `userFavorites` ref with the fetched data.
 */
onFavoritesResult((response) => {
  const favorites = response?.data?.me?.favorites;
  if (!favorites) {
    userFavorites.value = [];
    return;
  }
  if (Array.isArray(favorites)) {
    userFavorites.value = favorites;
    return;
  }
  if (typeof favorites === 'string') {
    try {
      userFavorites.value = JSON.parse(favorites);
    } catch (e) {
      console.warn('favorites string is not valid JSON:', favorites);
      userFavorites.value = [];
    }
    return;
  }
  userFavorites.value = [];
});

/**
 * Watcher: Observes changes to the `userFavorites` ref.
 * Syncs the updated favorites with the server, except during the first load.
 */
watch(
  userFavorites,
  () => {
    if (firstLoadDone.value) {
      // Sync updated favorites with the server using a debounced method
      debouncedUpdateFavorites(userFavorites.value);
    } else {
      // Mark the first load as completed
      firstLoadDone.value = true;
    }
  },
  {
    deep: true, // Ensure the watcher reacts to deep changes within the array
  },
);

// Is the device a touch device?
const touchDevice = ref('ontouchstart' in window || navigator.maxTouchPoints > 0);
</script>

<style scoped lang="scss">
.fav-draggable {
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
}
</style>
